import React, { lazy, Suspense, useState, useEffect } from "react";
import { Pagination, Table, Image } from "antd";
import { LoadingIcon } from "../../components/index.js";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;
const ReactPlayer = lazy(() => import("react-player"));

const Subplots = ({
  data,
  handleSubplotPageChange,
  subplotCount,
  subplotPage,
  setSubplotPage,
  currentPlot,
  siteMetadata,
}) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);

  // Controls whether Suspense shows the loading icon for the player
  const [showPlayerFallback, setShowPlayerFallback] = useState(true);

  // After 3 minutes, stop showing the loading spinner
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        setShowPlayerFallback(false);
      },
      3 * 60 * 1000,
    ); // 3 minutes

    return () => clearTimeout(timeoutId);
  }, []);

  const customSubplotColumn = siteMetadata?.subplot_table?.columns;
  const customFruitColumns = siteMetadata?.subplot_table?.fruit_columns;
  const addFruitIconInTable =
    siteMetadata?.subplot_table?.add_fruit_icon_in_table;

  const roundToTwoDecimals = (value) => {
    return Math.abs(value - Math.round(value)) < 1e-9
      ? Math.round(value)
      : Math.round(value * 100) / 100;
  };

  const processClusterData = (subplot) => {
    const finalData = [];
    subplot?.fruit_data?.forEach((clusterData) => {
      const clusterFruitData = clusterData?.Fruits || clusterData.fruits || [];
      const expanded = clusterFruitData.map((fruit, idx, arr) => {
        const fruitLen = arr.length;
        const rowSpan = idx === 0 ? fruitLen : 0;
        const remainingSpan = fruitLen - idx;
        return {
          ...clusterData,
          ...fruit,
          key: `${subplot?.id}${clusterData?.ClusterID}${fruit?.FruitID}`,
          rowSpan: rowSpan,
          remainingSpan: remainingSpan,
        };
      });
      finalData.push(...expanded);
    });

    // Round numeric values to 2 decimal places where applicable
    return finalData.map((item) => {
      const newItem = { ...item };
      for (const key in newItem) {
        if (typeof newItem[key] === "number") {
          newItem[key] = roundToTwoDecimals(newItem[key]);
        }
      }
      return newItem;
    });
  };

  const handleTableChange = (pagination) => {
    setPageSize(pagination.pageSize || DEFAULT_PAGE_SIZE);
    setCurrentPage(pagination.current || DEFAULT_PAGE);
  };

  const generateColumns = (
    finalData,
    subplot,
    pageSize,
    currentPage,
    sharedOnCell,
  ) => {
    let columns = [
      {
        title: "Cluster ID",
        dataIndex: "ClusterID",
        onCell: sharedOnCell,
      },
      {
        title: "Cluster Fruit Count",
        dataIndex: "Cluster Fruit Count",
        onCell: sharedOnCell,
      },
      {
        title: "Cluster Height (cm)",
        dataIndex: "Cluster Height (cm)",
        onCell: sharedOnCell,
      },
      {
        title: "Cluster Width (cm)",
        dataIndex: "Cluster Width (cm)",
        onCell: sharedOnCell,
      },
      {
        title: "Fruits",
        children: [
          { title: "Fruit ID", dataIndex: "FruitID" },
          {
            title: "Height (cm)",
            dataIndex: "Height (cm)",
          },
          { title: "Width (cm)", dataIndex: "Width (cm)" },
          {
            title: "Weight (g)",
            dataIndex: "Weight (g)",
          },
          { title: "Ripeness Idx", dataIndex: "Ripeness Idx" },
        ],
        hide: currentPlot?.is_loose,
      },
    ].filter((col) => !col.hide);

    let isClusterIDPresent = false;
    if (customSubplotColumn) {
      if (customSubplotColumn.includes("ClusterID")) {
        isClusterIDPresent = true;
      }
      columns = columns.filter((col) => col.title === "Fruits");
      customSubplotColumn.forEach((col) =>
        columns.push({
          title: col === "ClusterID" ? "Cluster ID" : col,
          dataIndex: col,
          onCell: sharedOnCell,
        }),
      );
    }

    if (customFruitColumns) {
      columns = columns.filter((col) => col.title !== "Fruits");
      if (isClusterIDPresent) {
        columns.push({
          title: "Fruits",
          children: customFruitColumns.map((col) => ({
            title: col === "FruitID" ? "Fruit ID" : col,
            dataIndex: col,
          })),
        });
      } else {
        columns.push({
          title: "Fruits",
          children: customFruitColumns.map((col) => ({
            title: col === "FruitID" ? "Fruit ID" : col,
            dataIndex: col,
            sorter: (a, b) => a[col] - b[col],
          })),
        });
      }
    }
    if (addFruitIconInTable) {
      columns.forEach((col) => {
        if (col.title === "Fruits") {
          const fruitIdIndex = col.children.findIndex(
            (child) => child.dataIndex.toLowerCase() === "fruitid",
          );

          const fruitImageColumn = {
            title: "Fruit Image",
            dataIndex: "Fruit Image",
            render: (text, record) => {
              return (
                record.fruit_url && (
                  <Image
                    width={50}
                    height={50}
                    src={record.fruit_url}
                    fallback="data:image/png;base64,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"
                  />
                )
              );
            },
          };

          if (fruitIdIndex !== -1) {
            col.children.splice(fruitIdIndex + 1, 0, fruitImageColumn);
          } else {
            col.children.push(fruitImageColumn);
          }
        }
      });
    }

    return columns;
  };

  return (
    <div className="mb-10" id="subplots">
      <p className="w-full text-center font-IBMPlexSansBold text-2xl mb-5 heading">
        Sub-plot Data
      </p>

      {data?.map((subplot, subplotIdx) => {
        const finalData = processClusterData(subplot);
        const sharedOnCell = (_, index) => {
          const trueIndex = (currentPage - 1) * pageSize + index;
          const span =
            index === 0
              ? finalData[trueIndex].remainingSpan
              : finalData[trueIndex].rowSpan;
          return { rowSpan: span };
        };
        const columns = generateColumns(
          finalData,
          subplot,
          pageSize,
          currentPage,
          sharedOnCell,
        );

        return (
          <div key={subplotIdx}>
            <p className="w-full font-IBMPlexSansBold text-xl mb-1">
              {subplot?.name}
            </p>
            <div className="flex mb-2 items-center justify-around">
              <Table
                className="mr-2 max-w-[60%] rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101"
                columns={columns}
                dataSource={finalData}
                onChange={handleTableChange}
                bordered
                pagination={{ position: ["bottomCenter"] }}
              />
              {subplot?.attachment_url && (
                <Suspense
                  fallback={
                    showPlayerFallback ? <LoadingIcon height="30px" /> : null
                  }
                >
                  <ReactPlayer
                    width="25%"
                    height="auto"
                    controls
                    src={subplot.attachment_url}
                  />
                </Suspense>
              )}
            </div>
          </div>
        );
      })}

      <Pagination
        hideOnSinglePage={true}
        className="flex justify-center hover:shadow-2xl transition-shadow duration-300 transform hover:scale-110"
        current={subplotPage}
        total={subplotCount}
        onChange={(page, _) => {
          handleSubplotPageChange(page);
        }}
      />
    </div>
  );
};

export default Subplots;
